import { IonLoading } from '@ionic/react';
import { sortBy } from 'lodash';
import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SkeletonPage } from '../../components/skeleton/skeleton-page';
import { logger } from '../../util/logger';
import {
  DirectConnectGateFailed,
  DirectConnectGateFailedProps,
} from './direct-connect-gate-failed';

export interface DirectConnectGateKeeperProps {
  children: ReactElement;
}

/**
 * The enum value is the priority of the gate.
 * If two or more gates report that something
 * should happen, show an error, etc then the
 * highest priority gate wins (1=highest).
 */
export enum DirectConnectGateSource {
  WIFI_GATE = 1,
  HEALTH_GATE = 2,
  DEACTIVATE_GATE = 3,
  PROVISION_GATE = 4,
  SYNCHRONIZE_GATE = 5,
}

interface LoaderConfig {
  show: boolean;
  message: string;
}

interface BackgroundConfig {
  show: boolean;
}

interface FailedConfig {
  show: boolean;
  props: Omit<DirectConnectGateFailedProps, 'isOpen'>;
}

export interface DirectConnectGateStatus {
  source: DirectConnectGateSource;
  failed: FailedConfig;
  loader: LoaderConfig;
  background: BackgroundConfig;
}

interface DirectConnectGateKeeperContextValue {
  reportGateStatus: (status: DirectConnectGateStatus) => void;
  hasFailedGate: boolean;
}

const DirectConnectGateKeeperContext = createContext<
  DirectConnectGateKeeperContextValue | undefined
>(undefined);

const DirectConnectGateKeeper = (props: DirectConnectGateKeeperProps) => {
  const { children } = props;

  const [statuses, setStatuses] = useState<DirectConnectGateStatus[]>([]);
  const [prioritizedGateStatus, setPrioritizedGateStatus] = useState<
    DirectConnectGateStatus | undefined
  >();

  const statusesRef = useRef<DirectConnectGateStatus[]>([]);

  statusesRef.current = statuses;

  const reportGateStatus = useCallback((status: DirectConnectGateStatus) => {
    logger.info('gate status reported', {
      status,
    });

    const filtered = statusesRef.current.filter(
      (s) => s.source !== status.source
    );

    const sorted = sortBy([...filtered, status], 'source');

    // go ahead and proactively update ref in case we receive another status
    statusesRef.current = sorted;

    // identify the status with higheset priority. this means the gate that
    // failed or the gate that is currently loading
    const prioritized = sorted.find((s) => s.failed.show || s.loader.show);

    setPrioritizedGateStatus(prioritized);

    // update statuses and sort by source which will allow us to prioritize
    // the gate failed dialog
    setStatuses(sorted);
  }, []);

  const hasFailedGate = prioritizedGateStatus?.failed.show ?? false;

  const contextValue = useMemo<DirectConnectGateKeeperContextValue>(() => {
    return {
      reportGateStatus,
      hasFailedGate,
    };
  }, [reportGateStatus, hasFailedGate]);

  const gateFailedProps = useMemo<DirectConnectGateFailedProps>(() => {
    if (prioritizedGateStatus && prioritizedGateStatus?.failed.show) {
      return {
        isOpen: true,
        ...prioritizedGateStatus.failed.props,
      };
    }

    // return dummy props so we can keep gate failed in dom and not display
    // until we have a failed gate
    return {
      isOpen: false,
      title: '',
      description: '',
      retry: async () => {},
    };
  }, [prioritizedGateStatus]);

  return (
    <>
      <IonLoading
        isOpen={prioritizedGateStatus?.loader.show ?? false}
        message={prioritizedGateStatus?.loader.message}
      />
      <DirectConnectGateKeeperContext.Provider value={contextValue}>
        {children}
      </DirectConnectGateKeeperContext.Provider>
      <DirectConnectGateFailed {...gateFailedProps} />
      {prioritizedGateStatus?.background.show && <SkeletonPage />}
    </>
  );
};

const useDirectConnectGateKeeper = () => {
  return useContext(
    DirectConnectGateKeeperContext
  ) as DirectConnectGateKeeperContextValue;
};

export { DirectConnectGateKeeper, useDirectConnectGateKeeper };
